import { getLeagueAssetUrl, getLeagueIdByTrophies, getTownHallAssetUrl } from "@acme/coc";
import { Button } from "@acme/ui/button";
import { Card, CardContent, CardHeader } from "@acme/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@acme/ui/dropdown-menu";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@acme/ui/table";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { MoreHorizontal, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AddPlayerDialogV2 } from "~/common/components/add-player-dialog-v2.js";
import PlayerIdentityLink from "~/common/components/player-identity-link.js";
import { ListViewCardsGrid } from "~/common/components/responsive-list-view.js";
import { ListViewToggle } from "~/common/components/list-view-toggle.js";
import { surfaceTableShell } from "~/common/components/surface-styles.js";
import { useListViewMode } from "~/common/hooks/use-list-view-mode.js";
import { api } from "~/common/utils/trpc.js";
import { PlayerOverviewCard } from "./_components/-player-overview-card.js";

export const Route = createFileRoute("/_authed/my-players/")({
  component: RouteComponent,
});

function LoadingSkeletonTable() {
  return (
    <div className={surfaceTableShell}>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Town Hall</TableHead>
            <TableHead>Trophies</TableHead>
            <TableHead>Best Trophies</TableHead>
            <TableHead>Exp. Level</TableHead>
            <TableHead>CW Stars</TableHead>
            <TableHead>CWL Stars</TableHead>
            <TableHead>Clan Game Points</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {Array.from({ length: 5 }).map((_, i) => (
            <TableRow key={i}>
              <TableCell>
                <div className="h-5 w-32 rounded bg-muted animate-pulse" />
              </TableCell>
              <TableCell>
                <div className="h-5 w-8 rounded bg-muted animate-pulse" />
              </TableCell>
              <TableCell>
                <div className="h-5 w-12 rounded bg-muted animate-pulse" />
              </TableCell>
              <TableCell>
                <div className="h-5 w-12 rounded bg-muted animate-pulse" />
              </TableCell>
              <TableCell>
                <div className="h-5 w-10 rounded bg-muted animate-pulse" />
              </TableCell>
              <TableCell>
                <div className="h-5 w-10 rounded bg-muted animate-pulse" />
              </TableCell>
              <TableCell>
                <div className="h-5 w-14 rounded bg-muted animate-pulse" />
              </TableCell>
              <TableCell>
                <div className="h-5 w-12 rounded bg-muted animate-pulse" />
              </TableCell>
              <TableCell className="text-right">
                <div className="h-8 w-8 rounded bg-muted animate-pulse ml-auto" />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function LoadingSkeletonCards() {
  return (
    <ListViewCardsGrid>
      {Array.from({ length: 6 }).map((_, i) => (
        <Card className="overflow-hidden" key={i}>
          <CardHeader className="pb-2 pr-10">
            <div className="flex items-start gap-3">
              <div className="size-10 rounded bg-muted animate-pulse shrink-0" />
              <div className="h-5 flex-1 rounded bg-muted animate-pulse" />
            </div>
          </CardHeader>
          <CardContent className="flex flex-col gap-2 pt-0">
            {Array.from({ length: 6 }).map((_, j) => (
              <div className="h-4 w-full rounded bg-muted animate-pulse" key={j} />
            ))}
          </CardContent>
        </Card>
      ))}
    </ListViewCardsGrid>
  );
}

function RouteComponent() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { effectiveView, viewMode, setViewMode, isBelowLg } = useListViewMode();
  const playersQueryOptions = api.player.getAllForOverviewByUserId.queryOptions();
  const { data: players, isPending: isLoading } = useQuery(playersQueryOptions);
  const [addPlayerDialogOpen, setAddPlayerDialogOpen] = useState(false);

  const unlinkFromUserMutation = useMutation(
    api.player.unlinkFromUser.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: api.player.getAllForOverviewByUserId.queryKey(),
        });
      },
    })
  );

  const handleUnlinkFromUser = (playerTag: string) => {
    unlinkFromUserMutation.mutate({ playerTag });
  };

  if (isLoading) {
    return (
      <div className="container mx-auto py-8 px-4">
        <div className="flex items-center justify-between mb-4 gap-2">
          <div className="h-9 w-48 rounded-md bg-muted animate-pulse" />
          <div className="flex gap-2">
            <ListViewToggle isBelowLg={isBelowLg} setViewMode={setViewMode} viewMode={viewMode} />
            <div className="h-9 w-28 rounded-md bg-muted animate-pulse" />
          </div>
        </div>
        {effectiveView === "table" ? <LoadingSkeletonTable /> : <LoadingSkeletonCards />}
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        <h1 className="text-3xl font-bold">{t("app.myPlayers.title")}</h1>
        <div className="flex items-center gap-2">
          <ListViewToggle isBelowLg={isBelowLg} setViewMode={setViewMode} viewMode={viewMode} />
          <Button onClick={() => setAddPlayerDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            {t("app.myPlayers.addPlayer")}
          </Button>
        </div>
      </div>

      {effectiveView === "table" ? (
        <div className={surfaceTableShell}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("app.myPlayers.table.name")}</TableHead>
                <TableHead>{t("app.myPlayers.table.townHall")}</TableHead>
                <TableHead>{t("app.myPlayers.table.trophies")}</TableHead>
                <TableHead>{t("app.myPlayers.table.bestTrophies")}</TableHead>
                <TableHead>{t("app.myPlayers.table.expLevel")}</TableHead>
                <TableHead>{t("app.myPlayers.table.warStars")}</TableHead>
                <TableHead>{t("app.myPlayers.table.warLeagueStars")}</TableHead>
                <TableHead>{t("app.myPlayers.table.clanGamePoints")}</TableHead>
                <TableHead className="text-right">{t("app.myPlayers.table.actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {players?.map((player) => {
                const bestLeagueId = getLeagueIdByTrophies(player.bestTrophies);
                return (
                  <TableRow key={player.playerTag}>
                    <TableCell className="font-medium">
                      <PlayerIdentityLink
                        playerName={player.name}
                        playerTag={player.playerTag}
                        showTag={false}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <img
                          alt=""
                          className="h-6 w-6 object-contain"
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                          src={getTownHallAssetUrl(player.townHallLevel)}
                        />
                        <span>{player.townHallLevel}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {player.league != null && (
                          <img
                            alt=""
                            className="h-6 w-6 object-contain"
                            onError={(e) => {
                              e.currentTarget.style.display = "none";
                            }}
                            src={getLeagueAssetUrl(player.league)}
                          />
                        )}
                        <span>{player.trophies}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {bestLeagueId != null && (
                          <img
                            alt=""
                            className="h-6 w-6 object-contain"
                            onError={(e) => {
                              e.currentTarget.style.display = "none";
                            }}
                            src={getLeagueAssetUrl(bestLeagueId)}
                          />
                        )}
                        <span>{player.bestTrophies ?? "–"}</span>
                      </div>
                    </TableCell>
                    <TableCell>{player.expLevel}</TableCell>
                    <TableCell>{player.warStars}</TableCell>
                    <TableCell>{player.warLeagueStars ?? "–"}</TableCell>
                    <TableCell>{player.clanGamePoints ?? "–"}</TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button className="h-8 w-8 p-0" onClick={(e) => e.stopPropagation()} variant="ghost">
                            <span className="sr-only">{t("app.myPlayers.openMenu")}</span>
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                          <DropdownMenuItem
                            disabled={unlinkFromUserMutation.isPending}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleUnlinkFromUser(player.playerTag);
                            }}
                            variant="destructive">
                            <Trash2 className="size-4" />
                            {t("app.myPlayers.removePlayer")}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      ) : (
        <ListViewCardsGrid>
          {players?.map((player) => (
            <PlayerOverviewCard
              isUnlinkPending={unlinkFromUserMutation.isPending}
              key={player.playerTag}
              onUnlinkFromUser={handleUnlinkFromUser}
              player={player}
            />
          ))}
        </ListViewCardsGrid>
      )}
      <AddPlayerDialogV2 onOpenChange={setAddPlayerDialogOpen} open={addPlayerDialogOpen} />
    </div>
  );
}
